// frontend/src/components/LegalModal.jsx
import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * 🔄 REMPLACEMENT DES VARIABLES DYNAMIQUES
 * Remplace les placeholders [VARIABLE] par les vraies valeurs de la config
 * Ex: [ADRESSE] → config.adresse
 */
const replaceVariables = (text, config) => {
  if (!text || !config) return text;
  
  // Mapping des variables vers les champs de config
  const variables = {
    '[DATE]': new Date().toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    }),
    '[NOM_FERME]': config.nom_ferme || 'La Ferme du Petit Essart',
    '[ADRESSE]': config.adresse || '',
    '[EMAIL]': config.email_contact || '',
    '[TÉLÉPHONE]': config.telephone || '',
    '[TELEPHONE]': config.telephone || '', // Variante sans accent
    '[SIRET]': config.siret || '',
    '[HORAIRES]': config.horaires_distribution || 'Se renseigner à la ferme',
    '[DELAI_MODIFICATION]': config.delai_modification_reservation || '24',
    '[X]': config.delai_modification_reservation || '24', // Alias pour [X] heures
  };
  
  // Remplacement de toutes les variables
  let result = text;
  Object.keys(variables).forEach(placeholder => {
    const value = variables[placeholder];
    if (value) {
      // Remplacement global avec regex (toutes les occurrences)
      const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      result = result.replace(regex, value);
    }
  });
  
  return result;
};

/**
 * Convertisseur Markdown simple vers HTML
 * Gère les éléments de base : titres, gras, italique, listes, liens
 */
const markdownToHtml = (markdown) => {
  if (!markdown) return '';
  
  let html = markdown
    // Titres
    .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold mt-6 mb-3">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-8 mb-4">$1</h1>')
    
    // Gras et italique
    .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    
    // Liens
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-green-600 hover:text-green-700 underline">$1</a>')
    
    // Listes à puces
    .replace(/^\- (.*$)/gim, '<li class="ml-4">$1</li>')
    .replace(/(<li class="ml-4">.*<\/li>)/s, '<ul class="list-disc ml-6 my-2">$1</ul>')
    
    // Listes numérotées
    .replace(/^\d+\. (.*$)/gim, '<li class="ml-4">$1</li>')
    
    // Paragraphes (lignes vides = nouveau paragraphe)
    .split('\n\n')
    .map(para => {
      if (para.trim() && !para.startsWith('<')) {
        return `<p class="mb-3">${para}</p>`;
      }
      return para;
    })
    .join('\n');
  
  return html;
};

const LegalModal = ({ isOpen, onClose, title, content, config }) => {
  // Fermer avec Échap
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Bloquer le scroll du body
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // ⚡ ÉTAPE 1 : Remplacer les variables dynamiques [ADRESSE], [EMAIL], etc.
  const contentWithVariables = replaceVariables(content, config);
  
  // ⚡ ÉTAPE 2 : Convertir markdown en HTML
  const htmlContent = markdownToHtml(contentWithVariables);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 animate-fadeIn">
      <div 
        className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-green-50 to-emerald-50">
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors p-2 hover:bg-white rounded-lg"
            aria-label="Fermer"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {content ? (
            <div 
              className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg mb-2">📄</p>
              <p>Aucun contenu disponible</p>
              <p className="text-sm mt-2">
                L'administrateur n'a pas encore configuré cette page
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 text-center">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            Fermer
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default LegalModal;